import * as fs from "node:fs";
import * as path from "node:path";
import type { Code, Nodes, Parent, Root } from "mdast";
import { fromMarkdown } from "mdast-util-from-markdown";

/**
 * One row in the fenced-sample extraction report — describes a single
 * fenced TypeScript code block found in a markdown page.
 */
export interface FencedSampleRecord {
  /**
   * Path to the markdown file the sample was extracted from. Resolved
   * relative to the configured `docsRoot` so reports are stable
   * across machines (no absolute prefixes leak through).
   */
  readonly docPath: string;

  /**
   * 1-indexed line number of the opening fence (the line that begins
   * with the three backticks), as reported by the markdown parser.
   */
  readonly line: number;

  /**
   * 0-indexed position of this fenced block among all fenced TS-shaped
   * blocks in the same markdown file. Useful when a single page has
   * multiple samples and the consumer needs to disambiguate findings.
   */
  readonly fenceIndex: number;

  /**
   * Language tag as written on the opening fence (e.g. `ts`,
   * `typescript`, `tsx`, `typescriptreact`). Always lower-cased.
   */
  readonly lang: SampleLang;

  /**
   * Raw source of the code block, exactly as written between the
   * fences. Trailing newlines are preserved so the compiler sees the
   * same input the human author intended.
   */
  readonly code: string;

  /**
   * `true` when the sample was tagged as opt-out (via fence meta or
   * an in-source pragma) and the compilation phase should skip it.
   * Skipped samples are still surfaced in the extraction report so
   * downstream consumers can audit them; the {@link compileFencedSamples}
   * helper filters them out before invoking `tsc`.
   */
  readonly skipped: boolean;

  /**
   * Short description of why a sample was skipped. Empty string when
   * `skipped` is `false`.
   */
  readonly skipReason: string;
}

/**
 * Recognized fenced-block language tags that the extractor treats as
 * compilable TypeScript samples.
 */
export const SampleLang = {
  Ts: "ts",
  Typescript: "typescript",
  Tsx: "tsx",
  Typescriptreact: "typescriptreact",
} as const;

/**
 * String-literal type matching the values of {@link SampleLang}.
 */
export type SampleLang = (typeof SampleLang)[keyof typeof SampleLang];

/**
 * Options accepted by {@link extractFencedSamples}.
 */
export interface ExtractFencedSamplesOptions {
  /**
   * Absolute or repo-relative path to the docs root that should be
   * walked for `*.md` files. Defaults to `docs/src/content/docs/`
   * relative to the current working directory when omitted.
   *
   * @default "docs/src/content/docs"
   */
  readonly docsRoot?: string;

  /**
   * When `true`, fenced blocks tagged `tsx` or `typescriptreact` are
   * included in the report. Defaults to `true` because the docs-sync
   * scan phase is JSX-aware; flip to `false` to scope a run to plain
   * TypeScript only.
   *
   * @default true
   */
  readonly includeTsx?: boolean;
}

const DEFAULT_DOCS_ROOT = "docs/src/content/docs";

const PLAIN_LANGS = new Set<SampleLang>([SampleLang.Ts, SampleLang.Typescript]);
const JSX_LANGS = new Set<SampleLang>([
  SampleLang.Tsx,
  SampleLang.Typescriptreact,
]);

/**
 * Walks every `*.md` file under the configured docs root and returns
 * one record per fenced TypeScript code block.
 *
 * The walker is AST-based — markdown is parsed with
 * `mdast-util-from-markdown` and only `code` (fenced + indented)
 * nodes are inspected. The `lang` attribute on the AST node is
 * matched against {@link SampleLang}; fences tagged `ts`,
 * `typescript`, `tsx`, or `typescriptreact` produce records.
 * Anything else (`bash`, `json`, untagged, etc.) is skipped.
 *
 * Skip detection. A sample is recorded with `skipped: true` when
 * either of these conditions holds:
 *
 * - The fence's `meta` string contains the word `skip` or `no-check`
 *   (case-insensitive) — i.e. either token follows the language tag
 *   on the opening fence line.
 * - The sample's first non-blank line is the comment `// @no-check`
 *   (case-insensitive on the directive name; the leading `//` is
 *   required).
 *
 * @param options - Extraction options. All fields are optional; pass
 *                  an empty object to use defaults.
 */
export function extractFencedSamples(
  options: ExtractFencedSamplesOptions = {},
): Array<FencedSampleRecord> {
  const docsRoot = path.resolve(options.docsRoot ?? DEFAULT_DOCS_ROOT);
  const includeTsx = options.includeTsx ?? true;

  if (!fs.existsSync(docsRoot)) {
    return [];
  }

  const records: Array<FencedSampleRecord> = [];
  for (const absolutePath of walkMarkdownFiles(docsRoot)) {
    const docPath = toPosix(path.relative(docsRoot, absolutePath));
    const source = fs.readFileSync(absolutePath, "utf-8");
    const tree = fromMarkdown(stripYamlFrontmatter(source));
    let fenceIndex = 0;
    collectFencedCode(tree, (node) => {
      const lang = normalizeLang(node.lang);
      if (lang === undefined) {
        return;
      }
      if (!includeTsx && JSX_LANGS.has(lang)) {
        return;
      }
      if (!PLAIN_LANGS.has(lang) && !JSX_LANGS.has(lang)) {
        return;
      }
      const start = node.position?.start;
      if (!start) {
        return;
      }
      const skip = detectSkip(node);
      records.push({
        docPath,
        line: start.line,
        fenceIndex,
        lang,
        code: node.value ?? "",
        skipped: skip.skipped,
        skipReason: skip.reason,
      });
      fenceIndex += 1;
    });
  }

  records.sort((a, b) => {
    if (a.docPath !== b.docPath) {
      return a.docPath.localeCompare(b.docPath);
    }
    if (a.line !== b.line) {
      return a.line - b.line;
    }
    return a.fenceIndex - b.fenceIndex;
  });

  return records;
}

function walkMarkdownFiles(root: string): Array<string> {
  const out: Array<string> = [];
  const stack: Array<string> = [root];
  while (stack.length > 0) {
    const dir = stack.pop()!;
    let entries: Array<fs.Dirent>;
    try {
      entries = fs.readdirSync(dir, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const entry of entries) {
      const full = path.join(dir, entry.name);
      if (entry.isDirectory()) {
        stack.push(full);
      } else if (entry.isFile() && full.toLowerCase().endsWith(".md")) {
        out.push(full);
      }
    }
  }
  out.sort();
  return out;
}

function collectFencedCode(tree: Root, visit: (node: Code) => void): void {
  walk(tree);

  function walk(node: Nodes): void {
    if (node.type === "code") {
      visit(node);
      return;
    }
    if (node.type === "html") {
      return;
    }
    const parent = node as Partial<Parent>;
    if (Array.isArray(parent.children)) {
      for (const child of parent.children) {
        walk(child as Nodes);
      }
    }
  }
}

function normalizeLang(raw: string | null | undefined): SampleLang | undefined {
  if (!raw) {
    return undefined;
  }
  const lower = raw.toLowerCase();
  switch (lower) {
    case "ts":
      return SampleLang.Ts;
    case "typescript":
      return SampleLang.Typescript;
    case "tsx":
      return SampleLang.Tsx;
    case "typescriptreact":
      return SampleLang.Typescriptreact;
    default:
      return undefined;
  }
}

const META_SKIP_PATTERN = /\b(skip|no-check)\b/i;
const PRAGMA_NO_CHECK_PATTERN = /^\s*\/\/\s*@no-check\b/i;

interface SkipDetection {
  readonly skipped: boolean;
  readonly reason: string;
}

function detectSkip(node: Code): SkipDetection {
  const meta = node.meta ?? "";
  const metaMatch = META_SKIP_PATTERN.exec(meta);
  if (metaMatch) {
    return { skipped: true, reason: `meta:${metaMatch[1].toLowerCase()}` };
  }
  const code = node.value ?? "";
  const firstLine = firstNonBlankLine(code);
  if (firstLine !== undefined && PRAGMA_NO_CHECK_PATTERN.test(firstLine)) {
    return { skipped: true, reason: "pragma:@no-check" };
  }
  return { skipped: false, reason: "" };
}

function firstNonBlankLine(source: string): string | undefined {
  for (const line of source.split("\n")) {
    if (line.trim().length > 0) {
      return line;
    }
  }
  return undefined;
}

function toPosix(p: string): string {
  return p.split(path.sep).join("/");
}

/**
 * Strip leading YAML frontmatter (`--- ... ---`) from a markdown
 * source while preserving line numbers. Without the dedicated
 * frontmatter micromark extension, `mdast-util-from-markdown`
 * parses `---` as a thematic break and the YAML body as a heading +
 * paragraph — which can cause the parser to misreport positions for
 * subsequent fenced blocks. The fence-content lines are replaced
 * with blank lines (not removed) so reported `line` numbers continue
 * to point at the correct position in the original source.
 */
function stripYamlFrontmatter(source: string): string {
  if (!source.startsWith("---")) {
    return source;
  }
  const lines = source.split("\n");
  if (lines[0] !== "---") {
    return source;
  }
  let endIndex = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i] === "---") {
      endIndex = i;
      break;
    }
  }
  if (endIndex < 0) {
    return source;
  }
  for (let i = 0; i <= endIndex; i++) {
    lines[i] = "";
  }
  return lines.join("\n");
}
